// src/components/tables/PointageTable.vue
<template>
  <div class="pointage-table">
    <div v-if="loading" class="table-loading">
      <div class="loader"></div>
      <p>Chargement des données...</p>
    </div>
    
    <div v-else-if="!data || data.length === 0" class="no-data">
      <span class="material-icons">info</span>
      <p>Aucun pointage disponible aujourd'hui</p>
    </div>
    
    <div v-else class="table-container">
      <!-- Actions du tableau -->
      <div class="table-actions">
        <div class="table-filter">
          <span class="material-icons">filter_list</span>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Filtrer..." 
            class="filter-input"
          />
        </div>
        <div class="table-export">
          <button class="export-btn pdf" @click="exportPDF">
            <span class="material-icons">picture_as_pdf</span> PDF
          </button>
          <button class="export-btn excel" @click="exportExcel">
            <span class="material-icons">table_chart</span> Excel
          </button>
        </div>
      </div>
      
      <!-- Tableau -->
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th class="center">#</th>
              <th class="sortable" @click="sortBy('personnel')">Agent
                <span class="sort-icon material-icons" v-if="sortColumn === 'personnel'">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </span>
              </th>
              <th class="center sortable" @click="sortBy('motif')">Motif
                <span class="sort-icon material-icons" v-if="sortColumn === 'motif'">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </span>
              </th>
              <th class="center sortable" @click="sortBy('heure_chaine')">Arrivée
                <span class="sort-icon material-icons" v-if="sortColumn === 'heure_chaine'">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </span>
              </th>
              <th class="center sortable" @click="sortBy('heure_depart')">Départ
                <span class="sort-icon material-icons" v-if="sortColumn === 'heure_depart'">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </span>
              </th>
              <th class="center">Durée</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in paginatedData" :key="i" :class="getRowClass(i)">
              <td class="center">{{ (currentPage - 1) * pageSize + i + 1 }}</td>
              <td class="left">
                <div class="personnel-info">
                  {{ p.personnel || '' }}
                  <span 
                    v-if="p.mvt > 0" 
                    class="mvt-indicator" 
                    title="Mouvement(s) aujourd'hui"
                  >🔄</span>
                </div>
                <div class="direction-info" v-if="p.grade">
                  <span class="direction-tag">{{ p.grade }}</span>
                </div>
              </td>
              <td class="center">
                <span :class="getMotifClass(p.motif)">{{ p.motif || '' }}</span>
              </td>
              <td class="center">{{ p.heure_chaine || '' }}</td>
              <td class="center">{{ p.heure_depart || '' }}</td>
              <td class="center bold">{{ calcHours(p.heure_chaine, p.heure_depart) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination -->
      <div class="pagination">
        <div class="pagination-stats">
          Affichage de {{ Math.min((currentPage - 1) * pageSize + 1, filteredData.length) }} à 
          {{ Math.min(currentPage * pageSize, filteredData.length) }} sur {{ filteredData.length }} pointages
        </div>
        <div class="pagination-controls">
          <button 
            class="pagination-btn first" 
            :disabled="currentPage === 1"
            @click="changePage(1)"
          >
            <span class="material-icons">first_page</span>
          </button>
          <button 
            class="pagination-btn prev" 
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            <span class="material-icons">chevron_left</span>
          </button>
          
          <div class="pagination-pages">
            <button 
              v-for="page in visiblePageNumbers" 
              :key="page" 
              @click="changePage(page)"
              class="page-number"
              :class="{ active: page === currentPage }"
            >{{ page }}</button>
          </div>
          
          <button 
            class="pagination-btn next" 
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            <span class="material-icons">chevron_right</span>
          </button>
          <button 
            class="pagination-btn last" 
            :disabled="currentPage === totalPages"
            @click="changePage(totalPages)"
          >
            <span class="material-icons">last_page</span>
          </button>
        </div>
        <div class="pagination-size">
          <label>Lignes par page:</label>
          <select v-model="innerPageSize" @change="changePageSize">
            <option v-for="size in [10, 25, 50, 100]" :key="size" :value="size">{{ size }}</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import * as XLSX from 'xlsx'
// Import de jspdf seulement - nous n'utiliserons pas jspdf-autotable qui cause des problèmes
import jsPDF from 'jspdf'

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  currentPage: {
    type: Number,
    default: 1
  },
  totalItems: {
    type: Number,
    default: 0
  },
  pageSize: {
    type: Number,
    default: 10
  }
})

const emit = defineEmits(['page-changed', 'page-size-changed'])

// Variables réactives locales pour éviter la mutation des props
const innerCurrentPage = ref(props.currentPage)
const innerPageSize = ref(props.pageSize)
const searchQuery = ref('')
const sortColumn = ref('personnel')
const sortDirection = ref('asc')

// Synchroniser les props avec les refs locales
watch(() => props.currentPage, (newVal) => {
  innerCurrentPage.value = newVal
})

watch(() => props.pageSize, (newVal) => {
  innerPageSize.value = newVal
})

// Filtrage des données
const filteredData = computed(() => {
  if (!Array.isArray(props.data)) return []
  
  let result = [...props.data]
  
  // Appliquer le filtre de recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase().trim()
    result = result.filter(p => {
      return (
        (p.personnel && p.personnel.toLowerCase().includes(query)) ||
        (p.motif && p.motif.toLowerCase().includes(query)) ||
        (p.direction && p.direction.toLowerCase().includes(query)) ||
        (p.heure_chaine && p.heure_chaine.includes(query)) ||
        (p.heure_depart && p.heure_depart.includes(query))
      )
    })
  }
  
  // Appliquer le tri
  result.sort((a, b) => {
    const aVal = a[sortColumn.value] || ''
    const bVal = b[sortColumn.value] || ''
    
    // Tri par ordre alphabétique ou numérique
    let comparison = 0
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal
    } else {
      comparison = String(aVal).localeCompare(String(bVal))
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison
  })
  
  return result
})

// Pagination des données filtrées
const paginatedData = computed(() => {
  const start = (innerCurrentPage.value - 1) * innerPageSize.value
  const end = start + innerPageSize.value
  return filteredData.value.slice(start, end)
})

// Nombre total de pages
const totalPages = computed(() => {
  return Math.ceil(filteredData.value.length / innerPageSize.value) || 1
})

// Générer les numéros de page à afficher
const visiblePageNumbers = computed(() => {
  const total = totalPages.value
  const current = innerCurrentPage.value
  const delta = 2 // nombre de pages à afficher avant et après la page courante
  
  let pages = []
  
  if (total <= 5) {
    // Si moins de 5 pages, afficher toutes les pages
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    // Toujours afficher la première page
    pages.push(1)
    
    // Calculer les pages à afficher autour de la page courante
    const start = Math.max(2, current - delta)
    const end = Math.min(total - 1, current + delta)
    
    // Ajouter un ellipsis si nécessaire avant les pages du milieu
    if (start > 2) {
      pages.push('...')
    }
    
    // Ajouter les pages du milieu
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    
    // Ajouter un ellipsis si nécessaire après les pages du milieu
    if (end < total - 1) {
      pages.push('...')
    }
    
    // Toujours afficher la dernière page
    pages.push(total)
  }
  
  return pages
})

// Changer de page
const changePage = (page) => {
  if (page === '...') return
  if (page < 1 || page > totalPages.value) return
  innerCurrentPage.value = page
  emit('page-changed', page)
}

// Changer la taille de page
const changePageSize = () => {
  innerCurrentPage.value = 1 // Revenir à la première page lors du changement de taille
  emit('page-size-changed', innerPageSize.value)
}

// Trier les données par colonne
const sortBy = (column) => {
  if (sortColumn.value === column) {
    // Inverser l'ordre si on clique sur la même colonne
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    // Définir la colonne et l'ordre par défaut (ascendant)
    sortColumn.value = column
    sortDirection.value = 'asc'
  }
  innerCurrentPage.value = 1 // Revenir à la première page après le tri
}

// Exporter en Excel
const exportExcel = () => {
  try {
    // Obtenir la date formatée
    const today = new Date()
    const dateString = today.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    })
    
    // Créer un nouveau workbook
    const workbook = XLSX.utils.book_new()
    
    // ===========================
    // PAGE DE GARDE STYLÉE
    // ===========================
    
    // Création du contenu de la page de garde avec plus de style
    const titleData = [
      ['RAPPORT DE POINTAGE'],
      [''],
      [''],
      [`Date du rapport: ${dateString}`],
      [''],
      [`Nombre d'enregistrements: ${filteredData.value.length}`],
      [''],
      [''],
      ['OPSOLVER - Système de gestion du personnel'],
      ['Document confidentiel - Usage interne uniquement']
    ]
    
    // Créer la feuille de titre
    const titleSheet = XLSX.utils.aoa_to_sheet(titleData)
    
    // Définir des fusions de cellules pour un meilleur rendu
    titleSheet['!merges'] = [
      // Fusion pour le titre principal (A1:G1)
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      // Fusion pour la date (A4:D4)
      { s: { r: 3, c: 0 }, e: { r: 3, c: 3 } },
      // Fusion pour le nombre d'enregistrements (A6:E6)
      { s: { r: 5, c: 0 }, e: { r: 5, c: 4 } },
      // Fusion pour le nom du système (A9:G9)
      { s: { r: 8, c: 0 }, e: { r: 8, c: 6 } },
      // Fusion pour la mention de confidentialité (A10:G10)
      { s: { r: 9, c: 0 }, e: { r: 9, c: 6 } }
    ]
    
    // Définir les largeurs des colonnes
    titleSheet['!cols'] = [
      { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ]
    
    // ===========================
    // FEUILLE DE DONNÉES STYLÉE
    // ===========================
    
    // Préparer les en-têtes
    const headerStyle = '000000FF';  // Style pour les cellules d'en-tête (fond bleu)
    const altRowStyle = 'CCCCCC';   // Style pour les lignes alternées (gris clair)
    
    // Création des lignes d'en-tête pour le tableau de données
    const dataHeader = ['OPSOLVER - RAPPORT DE POINTAGE']
    const dataSubheader = [`Généré le ${dateString}`]
    const emptyRow = ['']
    
    // En-têtes des colonnes
    const columnHeaders = ['N°', 'Agent', 'Grade', 'Motif', 'Arrivée', 'Départ', 'Durée']
    
    // Préparer les données du tableau
    const tableRows = filteredData.value.map((p, index) => [
      index + 1,
      p.personnel || '',
      p.grade || '',
      p.motif || '',
      p.heure_chaine || '',
      p.heure_depart || '',
      calcHours(p.heure_chaine, p.heure_depart)
    ])
    
    // Pied de page avec le total
    const dataFooter = [`Total des enregistrements: ${filteredData.value.length}`, '', '', '', '', '', '']
    
    // Assembler toutes les lignes
    const allDataRows = [
      dataHeader,           // Titre
      dataSubheader,        // Sous-titre avec date
      emptyRow,             // Espace
      columnHeaders,        // En-têtes des colonnes
      ...tableRows,         // Données du tableau
      emptyRow,             // Espace
      dataFooter            // Pied de page
    ]
    
    // Créer la feuille de données
    const dataSheet = XLSX.utils.aoa_to_sheet(allDataRows)
    
    // Définir les fusions de cellules
    dataSheet['!merges'] = [
      // Fusion du titre (A1:G1)
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      // Fusion du sous-titre (A2:G2)
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
      // Fusion du pied de page (A{dernierIndex}:G{dernierIndex})
      { s: { r: tableRows.length + 5, c: 0 }, e: { r: tableRows.length + 5, c: 6 } }
    ]
    
    // Définir les largeurs des colonnes
    dataSheet['!cols'] = [
      { wch: 5 },   // N°
      { wch: 30 },  // Agent
      { wch: 15 },  // Grade
      { wch: 15 },  // Motif
      { wch: 10 },  // Arrivée
      { wch: 10 },  // Départ
      { wch: 10 }   // Durée
    ]
    
    // Définir la hauteur des lignes (plus petite que par défaut)
    // Note: La propriété !rows est supportée de manière limitée dans XLSX
    dataSheet['!rows'] = [];
    for (let i = 0; i < allDataRows.length; i++) {
      // Appliquer une hauteur plus petite aux lignes de données
      if (i >= 4 && i < tableRows.length + 4) {
        dataSheet['!rows'][i] = { hpt: 12 }; // hauteur en points, réduite pour les données
      } else if (i === 0) {
        // Titre principal plus grand
        dataSheet['!rows'][i] = { hpt: 30 };
      } else {
        // Autres lignes (en-têtes, etc.)
        dataSheet['!rows'][i] = { hpt: 16 };
      }
    }
    
    // Ajouter les feuilles au classeur
    XLSX.utils.book_append_sheet(workbook, titleSheet, 'Rapport')
    XLSX.utils.book_append_sheet(workbook, dataSheet, 'Pointages')
    
    // Générer le fichier Excel avec un nom professionnel
    XLSX.writeFile(workbook, `OPSOLVER_Pointages_${dateString.replace(/\//g, '-')}.xlsx`)
  } catch (error) {
    console.error('Erreur lors de l\'export Excel:', error)
    alert('Une erreur est survenue lors de la génération du fichier Excel. Veuillez réessayer.')
  }
}

// Exporter en PDF
const exportPDF = () => {
  try {
    // Créer un nouveau document PDF au format A4
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })
    
    // Définir les marges et les dimensions
    const pageWidth = doc.internal.pageSize.width
    const pageHeight = doc.internal.pageSize.height
    const margin = 15
    const usableWidth = pageWidth - 2 * margin
    
    // Obtenir la date du jour formatée
    const today = new Date()
    const dateString = today.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    })
    const timeString = today.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
    
    // --- EN-TÊTE DU DOCUMENT ---
    
    // Arrière-plan de l'en-tête
    doc.setFillColor(41, 128, 185) // Bleu corporate
    doc.rect(0, 0, pageWidth, 35, 'F')
    
    // Titre du rapport
    doc.setFontSize(22)
    doc.setTextColor(255, 255, 255) // Texte blanc
    doc.setFont('helvetica', 'bold')
    doc.text('RAPPORT DE POINTAGE', margin, 20)
    
    // Date et heure du rapport
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Généré le ${dateString} à ${timeString}`, pageWidth - margin, 20, { align: 'right' })
    
    // Logo fictif
    /*doc.setFillColor(255, 255, 255)
    doc.circle(margin + 5, 15, 5, 'F')
    doc.setTextColor(41, 128, 185)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.text('OP', margin + 5, 18, { align: 'center' })*/
    
    // --- INFORMATIONS DU RAPPORT ---
    doc.setTextColor(80, 80, 80) // Texte gris foncé
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    doc.text('Informations du rapport:', margin, 45)
    
    // Cadre d'informations
    doc.setFillColor(245, 245, 245) // Gris très clair
    doc.roundedRect(margin, 48, usableWidth, 18, 1, 1, 'F')
    doc.setFontSize(10)
    doc.text(`• Nombre total d'enregistrements: ${filteredData.value.length}`, margin + 5, 55)
    doc.text(`• Période: Journée du ${dateString}`, margin + 5, 62)
    
    // --- TABLEAU DE DONNÉES CONSTRUIT MANUELLEMENT ---
    let startY = 80
    
    // Titre du tableau
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(41, 128, 185) // Bleu corporate
    doc.text('Liste des pointages', margin, startY - 5)
    
    // Paramètres du tableau
    const colWidths = [10, 45, 25, 25, 20, 20, 20]  // Largeurs des colonnes en mm
    const rowHeight = 6.5  // Hauteur des lignes en mm réduite
    const maxRowsPerPage = 25  // Nombre maximum de lignes par page
    let currentPage = 1
    
    // En-tête du tableau
    const headers = ['N°', 'Agent', 'Grade', 'Motif', 'Arrivée', 'Départ', 'Durée']
    
    // Fonction pour dessiner l'en-tête du tableau
    const drawTableHeader = (y) => {
      // Fond de l'en-tête
      doc.setFillColor(41, 128, 185)
      doc.rect(margin, y, usableWidth, rowHeight, 'F')
      
      // Texte de l'en-tête
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      
      // Calculer la position x de chaque colonne
      let xPos = margin
      for (let i = 0; i < headers.length; i++) {
        // Texte centré dans la colonne
        doc.text(headers[i], xPos + colWidths[i] / 2, y + rowHeight / 2 + 1, { align: 'center' })
        xPos += colWidths[i]
      }
    }
    
    // Fonction pour dessiner le pied de page
    const drawFooter = () => {
      // Pied de page
      doc.setFillColor(245, 245, 245)
      doc.rect(0, pageHeight - 20, pageWidth, 20, 'F')
      
      // Ligne de séparation
      doc.setDrawColor(200, 200, 200)
      doc.line(0, pageHeight - 20, pageWidth, pageHeight - 20)
      
      // Texte de pied de page
      doc.setTextColor(100, 100, 100)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text('OPSOLVER - Système de gestion du personnel', margin, pageHeight - 10)
      
      // Numéro de page
      doc.text(`Page ${currentPage}`, pageWidth - margin, pageHeight - 10, { align: 'right' })
      
      // Informations légales/confidentialité
      doc.setFontSize(7)
      doc.text('Document confidentiel - Usage interne uniquement', pageWidth / 2, pageHeight - 5, { align: 'center' })
    }
    
    // Dessiner l'en-tête de la première page
    drawTableHeader(startY)
    drawFooter()
    
    // Préparer les données
    const data = filteredData.value.map((p, index) => ([
      (index + 1).toString(),
      p.personnel || '',
      p.grade || '',
      p.motif || '',
      p.heure_chaine || '',
      p.heure_depart || '',
      calcHours(p.heure_chaine, p.heure_depart)
    ]))
    
    // Variables pour le suivi de la position
    let y = startY + rowHeight
    let rowCount = 0
    
    // Dessiner les lignes du tableau
    for (let i = 0; i < data.length; i++) {
      // Vérifier s'il faut ajouter une nouvelle page
      if (rowCount >= maxRowsPerPage || y + rowHeight > pageHeight - 25) {
        doc.addPage()
        currentPage++
        y = margin + 20  // Position en haut de la nouvelle page
        drawTableHeader(y)
        drawFooter()
        y += rowHeight
        rowCount = 0
      }
      
      // Alternance de couleur de fond
      if (i % 2 === 0) {
        doc.setFillColor(245, 245, 245)  // Gris clair
        doc.rect(margin, y, usableWidth, rowHeight, 'F')
      }
      
      // Dessin des bordures de cellule
      doc.setDrawColor(200, 200, 200)
      doc.rect(margin, y, usableWidth, rowHeight)
      
      // Dessin du contenu
      doc.setTextColor(60, 60, 60)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      
      // Dessiner le contenu de chaque cellule
      let xPos = margin
      for (let j = 0; j < data[i].length; j++) {
        // Formatage spécial selon le type de colonne
        if (j === 0) {  // Numéro
          doc.setFont('helvetica', 'normal')
          doc.text(data[i][j], xPos + colWidths[j] / 2, y + rowHeight / 2 + 1, { align: 'center' })
        } 
        else if (j === 1) {  // Agent
          doc.setFont('helvetica', 'normal')
          doc.text(data[i][j], xPos + 2, y + rowHeight / 2 + 1, { align: 'left' })
        }
        else if (j === data[i].length - 1) {  // Durée
          doc.setFont('helvetica', 'bold')
          doc.text(data[i][j], xPos + colWidths[j] / 2, y + rowHeight / 2 + 1, { align: 'center' })
        }
        else {  // Autres colonnes
          doc.setFont('helvetica', 'normal')
          doc.text(data[i][j], xPos + colWidths[j] / 2, y + rowHeight / 2 + 1, { align: 'center' })
        }
        
        // Dessiner les lignes verticales entre les cellules
        doc.line(xPos, y, xPos, y + rowHeight)
        xPos += colWidths[j]
      }
      doc.line(xPos, y, xPos, y + rowHeight)  // Dernière ligne verticale
      
      // Passer à la ligne suivante
      y += rowHeight
      rowCount++
    }
    
    // Sauvegarder le PDF avec un nom approprié
    doc.save(`OPSOLVER_Pointages_${dateString.replace(/\//g, '-')}.pdf`)
    
  } catch (error) {
    console.error('Erreur lors de l\'export PDF:', error)
    alert('Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.')
  }
}

const getRowClass = (index) => {
  return index % 2 === 0 ? 'row-even' : 'row-odd'
}

const getMotifClass = (motif) => {
  if (!motif) return 'badge'
  
  const motifStr = motif.toString().trim().toLowerCase()
  if (motifStr.includes('r')) return 'badge badge-warning'
  if (motifStr.includes('a')) return 'badge badge-danger'
  return 'badge badge-default'
}

const calcHours = (start, end) => {
  if (!start || !end) return '-'
  if (end === '00:00:00') return 'En service'
  
  const pad = v => v.toString().padStart(2, '0')
  const parse = t => {
    const [h, m, s] = t.split(':').map(Number)
    return (h || 0) * 60 + (m || 0)
  }
  
  let minutes = parse(end) - parse(start)
  if (minutes < 0) minutes += 24 * 60 // gestion passage minuit
  
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  
  if (h > 8 || (h === 8 && m > 0)) {
    return '8h+'
  }
  
  return `${h}h${m > 0 ? ' ' + pad(m) + 'min' : ''}`
}
</script>

<style scoped>
/* Styles généraux */
.pointage-table {
  position: relative;
  width: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

/* États de chargement et absence de données */
.table-loading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #666;
}

.table-loading p {
  margin-top: 15px;
  font-size: 14px;
}

.loader {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data {
  padding: 40px 20px;
  text-align: center;
  color: #777;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.no-data .material-icons {
  font-size: 40px;
  margin-bottom: 10px;
  color: #ccc;
}

.no-data p {
  margin: 0;
  font-size: 16px;
}

/* Container du tableau */
.table-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* Barre d'actions au-dessus du tableau */
.table-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #eee;
}

.table-filter {
  display: flex;
  align-items: center;
  flex: 1;
}

.table-filter .material-icons {
  color: #666;
  margin-right: 8px;
}

.filter-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 200px;
  transition: border-color 0.2s;
}

.filter-input:focus {
  outline: none;
  border-color: #3498db;
}

.table-export {
  display: flex;
  gap: 10px;
}

.export-btn {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.export-btn .material-icons {
  font-size: 16px;
  margin-right: 4px;
}

.export-btn.pdf {
  background-color: #e74c3c;
  color: white;
}

.export-btn.excel {
  background-color: #27ae60;
  color: white;
}

.export-btn:hover {
  opacity: 0.9;
}

/* Wrapper du tableau avec scrollbar horizontal si nécessaire */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

/* Table de données */
.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.data-table th {
  background-color: #f5f5f5;
  padding: 12px 16px;
  font-weight: 600;
  text-align: left;
  border-bottom: 2px solid #ddd;
  color: #333;
  position: sticky;
  top: 0;
  z-index: 10;
}

.data-table th.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-right: 24px; /* Espace pour l'icône de tri */
}

.data-table th.sortable:hover {
  background-color: #eee;
}

.sort-icon {
  font-size: 16px !important;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  color: #3498db;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.center {
  text-align: center;
}

.left {
  text-align: left;
}

.bold {
  font-weight: 600;
}

.row-even {
  background-color: #fff;
}

.row-odd {
  background-color: #f9f9f9;
}

.row-even:hover, .row-odd:hover {
  background-color: #f0f7ff;
}

/* Styles pour les informations du personnel */
.personnel-info {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.direction-info {
  margin-top: 4px;
}

.direction-tag {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  background-color: #e8f4fd;
  color: #1976d2;
  border-radius: 3px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Badges et indicateurs */
.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background-color: #eee;
  white-space: nowrap;
}

.badge-warning {
  background-color: #fff3cd;
  color: #856404;
}

.badge-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.mvt-indicator {
  color: #1976d2;
  font-size: 1.2em;
  margin-left: 5px;
  vertical-align: middle;
}

/* Pagination améliorée */
.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-top: 1px solid #eee;
  gap: 10px;
}

.pagination-stats {
  color: #666;
  font-size: 13px;
}

.pagination-controls {
  display: flex;
  align-items: center;
}

.pagination-pages {
  display: flex;
  align-items: center;
  margin: 0 5px;
}

.pagination-btn {
  background: none;
  border: 1px solid #e0e0e0;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  margin: 0 2px;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f5f5f5;
  border-color: #ccc;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.pagination-btn.first,
.pagination-btn.last {
  border-color: #e0e0e0;
}

.page-number {
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  border: 1px solid #e0e0e0;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0 2px;
  border-radius: 4px;
  font-size: 13px;
  transition: all 0.2s;
  color: #333;
}

.page-number:hover {
  background-color: #f5f5f5;
}

.page-number.active {
  background-color: #3498db;
  color: white;
  border-color: #3498db;
  font-weight: 500;
}

/* Sélecteur de taille de page */
.pagination-size {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.pagination-size label {
  margin-right: 8px;
  color: #666;
  font-size: 13px;
}

.pagination-size select {
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  background-color: white;
}

.pagination-size select:focus {
  outline: none;
  border-color: #3498db;
}

/* Responsive */
@media (max-width: 768px) {
  .pagination {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .pagination-controls,
  .pagination-stats,
  .pagination-size {
    width: 100%;
    justify-content: center;
    margin-bottom: 8px;
  }
  
  .table-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .table-filter,
  .table-export {
    width: 100%;
  }
  
  .filter-input {
    width: 100%;
  }
}
</style>